import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Modal, Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import { LEGAL_DOCUMENTS_VERSION, MARKETING_CONSENT_VERSION, takePendingSignupConsent } from '../../services/consents';
import { acceptedRegistrationDocuments, isForeignContour, marketingConsentDocuments } from '../../legal/accepted';
import { consentGateMode, legalConsentKeys, marketingConsentOffered, planGateSubmit, shouldSendMarketingConsent } from '../../services/consentLogic';
import { AgeChoice } from '../../screens/auth/AgeChoice';
import { AgeStep, initialAgeStep, nextAgeStep } from '../../screens/auth/ageChoiceLogic';
import { LegalConsentCheckbox, LegalDocKind, MarketingConsentCheckbox } from '../../screens/auth/LegalConsent';
import { accentTextOn, ON_ACCENT_TEXT } from '../../styles/accentText';
import { surfaceFill } from '../../styles/platformBackgrounds';

interface Props {
  /** Как открыть документ: на вебе — маршрут в новой вкладке, в приложении — модал. */
  onOpenDoc: (doc: LegalDocKind) => void;
  /** Модал с документом (только в нативной сборке — тексты не должны попадать в веб-чанк). */
  children?: React.ReactNode;
}

/**
 * CORE-1404 — обязательный экран «возраст + согласие» для аккаунта без них.
 *
 * Появляется, когда сервер отвечает `consentRequired` — в основном у новых
 * аккаунтов через Google/Apple: соцвход создаёт аккаунт до того, как человек
 * что-то отметил. Закрыть экран нельзя — только пройти или выйти. Если ответы
 * уже даны на форме регистрации до нажатия кнопки соцвхода, они отправляются
 * сами, без повторного вопроса.
 */
export const ConsentGateView: React.FC<Props> = ({ onOpenDoc, children }) => {
  const { user, isAuthenticated, refreshUser, logout } = useAuth();
  const { colors, isDark } = useTheme();
  const { t } = useI18n('auth');
  // CORE-1733 (возврат QA 23.10) — окно на вебе — непрозрачная поверхность v4
  // (была плоская #FFFFFF / #202226). Натив — прежний canvas.
  const cardFill = Platform.OS === 'web'
    ? surfaceFill(isDark ? 'dark' : 'light')
    : { backgroundColor: colors.canvas, borderColor: colors.border };

  const required = isAuthenticated && user?.consentRequired === true;
  const serverAgeGroup = user?.ageGroup ?? null;
  // CORE-1568 — повторное принятие пакета: свой заголовок, без рекламной галочки.
  const mode = consentGateMode(user?.consentReason);

  const [ageStep, setAgeStep] = useState<AgeStep>(initialAgeStep);
  const [legalAccepted, setLegalAccepted] = useState(false);
  // CORE-1657 — необязательное согласие на рекламные сообщения.
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removed, setRemoved] = useState(false);
  const autoTried = useRef(false);

  const chosenAge = ageStep.kind === 'done' ? ageStep.ageGroup : null;
  // Рекламу предлагаем только взрослым: подросткам сервер её и не примет.
  // CORE-1929 — и только в российском контуре (решение владельца 29.10).
  const marketingOffered = mode.offerMarketing && marketingConsentOffered(serverAgeGroup ?? chosenAge, isForeignContour());

  const submit = useCallback(
    async (input: { chosenAge: typeof chosenAge; legalAccepted: boolean; marketingAccepted: boolean }) => {
      const plan = planGateSubmit({ serverAgeGroup, ...input });
      if (plan.kind === 'error') {
        // CORE-1932 — «примите …» в той же формулировке, что и галочка.
        setError(t(plan.messageKey === 'legal.mustAccept' ? legalConsentKeys(!isForeignContour()).mustAccept : plan.messageKey));
        return;
      }
      setSaving(true);
      setError(null);
      try {
        if (plan.kind === 'under14') {
          try {
            await api.declareAge('under14');
          } catch {
            // Ожидаемый ответ — 403 AGE_NOT_ALLOWED: аккаунт поставлен на удаление.
          }
          setRemoved(true);
          await logout();
          return;
        }
        await api.grantConsent({
          type: 'personal_data',
          documentVersion: LEGAL_DOCUMENTS_VERSION,
          documents: acceptedRegistrationDocuments(),
          source: 'social_gate',
        });
        if (plan.declareAge) await api.declareAge(plan.declareAge);
        // CORE-1657 — после возраста: сервер не принимает рекламное согласие от 14–17.
        if (mode.offerMarketing && shouldSendMarketingConsent(input.marketingAccepted, serverAgeGroup ?? plan.declareAge, isForeignContour())) {
          try {
            await api.grantConsent({
              type: 'marketing',
              documentVersion: MARKETING_CONSENT_VERSION,
              documents: marketingConsentDocuments(),
              source: 'social_gate',
            });
          } catch {
            // Необязательное согласие не должно запирать человека на этом экране:
            // его можно дать позже в настройках.
          }
        }
        await refreshUser();
      } catch {
        setError(t('consentGate.error'));
      } finally {
        setSaving(false);
      }
    },
    [serverAgeGroup, mode.offerMarketing, logout, refreshUser, t],
  );

  // CORE-1931 — «Выйти» без согласия: аккаунт, созданный соцвходом, сервер удаляет
  // сразу (данных, кроме почты и имени от провайдера, у него нет). Аккаунт с
  // согласием или любыми действиями сервер не трогает (409) — выходим в любом случае.
  const declineAndLogout = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (mode.deleteOnDecline) await api.deleteUnconsentedAccount();
    } catch {
      // Не подошёл под удаление или сеть: аккаунт без согласия уберёт фоновая очистка.
    } finally {
      setSaving(false);
    }
    await logout();
  }, [saving, logout, mode.deleteOnDecline]);

  // Ответы с формы регистрации, данные до соцвхода, — отправить без вопроса.
  useEffect(() => {
    if (!required || autoTried.current) return;
    autoTried.current = true;
    const pending = takePendingSignupConsent();
    if (!pending) return;
    if (pending.ageGroup) setAgeStep({ kind: 'done', ageGroup: pending.ageGroup });
    setLegalAccepted(pending.legalAccepted);
    setMarketingAccepted(pending.marketingAccepted);
    if (pending.legalAccepted && (pending.ageGroup || serverAgeGroup)) {
      void submit({ chosenAge: pending.ageGroup, legalAccepted: true, marketingAccepted: pending.marketingAccepted });
    }
  }, [required, serverAgeGroup, submit]);

  useEffect(() => {
    if (!required) autoTried.current = false;
  }, [required]);

  if (removed && !required) {
    return (
      <Modal visible transparent animationType="fade" onRequestClose={() => setRemoved(false)}>
        <View style={[styles.overlay, { backgroundColor: 'rgba(0,0,0,0.55)' }]}>
          <View style={[styles.card, cardFill]}>
            <Text style={[styles.body, { color: colors.textPrimary }]}>{t('consentGate.under14Removed')}</Text>
            <TouchableOpacity style={styles.primary} onPress={() => setRemoved(false)} accessibilityRole="button">
              <Text style={styles.primaryText}>{t('legal.close')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    );
  }

  if (!required) return null;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={() => { /* закрыть нельзя — только пройти или выйти */ }}>
      <View style={[styles.overlay, { backgroundColor: 'rgba(0,0,0,0.55)' }]}>
        <View
          testID="consentGate"
          style={[styles.card, cardFill]}
        >
          <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
            <Text style={[styles.title, { color: colors.textPrimary }]}>{t(mode.titleKey)}</Text>
            <Text style={[styles.body, { color: colors.textSecondary }]}>{t(mode.bodyKey)}</Text>

            {!serverAgeGroup && (
              <AgeChoice
                step={ageStep}
                onAnswer={(answer) => { setAgeStep((s) => nextAgeStep(s, answer)); setError(null); }}
                textColor={colors.textPrimary}
                mutedColor={colors.textSecondary}
                // CORE-1733 — «Изменить» и «Назад» — ссылки: #E85D04 на поверхности давал ~3.5:1.
                accentColor={accentTextOn(SOLAR)}
                borderColor={colors.border}
              />
            )}

            {!(ageStep.kind === 'done' && ageStep.ageGroup === 'under14') && (
              <LegalConsentCheckbox
                checked={legalAccepted}
                onToggle={() => { setLegalAccepted((v) => !v); setError(null); }}
                onOpenDoc={onOpenDoc}
                textColor={colors.textSecondary}
                linkColor={accentTextOn(SOLAR)}
                borderColor={colors.border}
                fontSize={14}
              />
            )}

            {marketingOffered && (
              <MarketingConsentCheckbox
                checked={marketingAccepted}
                onToggle={() => setMarketingAccepted((v) => !v)}
                onOpenDoc={onOpenDoc}
                textColor={colors.textSecondary}
                accentColor={SOLAR}
                linkColor={accentTextOn(SOLAR)}
                borderColor={colors.border}
                fontSize={14}
              />
            )}

            {!!error && <Text style={styles.error}>{error}</Text>}

            <TouchableOpacity
              testID="consentGateContinue"
              style={[styles.primary, saving && styles.disabled]}
              disabled={saving}
              onPress={() => void submit({ chosenAge, legalAccepted, marketingAccepted })}
              accessibilityRole="button"
            >
              {saving ? <ActivityIndicator color={ON_ACCENT_TEXT} /> : <Text style={styles.primaryText}>{t('consentGate.continue')}</Text>}
            </TouchableOpacity>

            <TouchableOpacity
              testID="consentGateLogout"
              onPress={() => void declineAndLogout()}
              disabled={saving}
              hitSlop={8}
              accessibilityRole="button"
              style={styles.secondary}
            >
              <Text style={[styles.secondaryText, { color: colors.textSecondary }]}>{t('consentGate.logout')}</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
      {children}
    </Modal>
  );
};

const SOLAR = '#E85D04';

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  card: {
    width: '100%',
    maxWidth: 460,
    maxHeight: '90%',
    borderRadius: 20,
    borderWidth: 1,
    ...(Platform.OS === 'web' ? { boxShadow: '0 12px 40px rgba(0,0,0,0.25)' as any } : {}),
  },
  content: {
    padding: 24,
    gap: 18,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
  },
  body: {
    fontSize: 15,
    lineHeight: 22,
  },
  error: {
    color: '#D93025',
    fontSize: 14,
  },
  primary: {
    backgroundColor: SOLAR,
    borderRadius: 100,
    paddingVertical: 14,
    alignItems: 'center',
  },
  primaryText: {
    color: ON_ACCENT_TEXT,
    fontSize: 16,
    fontWeight: '600',
  },
  disabled: {
    opacity: 0.6,
  },
  secondary: {
    alignItems: 'center',
  },
  secondaryText: {
    fontSize: 14,
    textDecorationLine: 'underline',
  },
});
